import type { DevicePayload } from '../lib/api';

interface DeviceCardProps {
  device: DevicePayload;
  onStart: (uuid: string) => void;
  onStop: (uuid: string) => void;
  busy?: boolean;
}

type StatusKind = 'available' | 'charging' | 'paused' | 'offline' | 'error';

interface ResolvedStatus {
  kind: StatusKind;
  label: string;
}

function resolveStatus(device: DevicePayload): ResolvedStatus {
  const state = device.state || {};
  const hasSessions = device.has_sessions !== false;
  const stateEmpty = Object.keys(state).length === 0;

  if (stateEmpty) return { kind: 'offline', label: 'Connecting…' };

  if (!hasSessions) {
    return state.active
      ? { kind: 'charging', label: 'Active' }
      : { kind: 'available', label: 'Idle' };
  }

  if (device.service_type === 'easee' && typeof state.chargerOpMode === 'number') {
    switch (state.chargerOpMode) {
      case 1: return { kind: 'available', label: 'Disconnected' };
      case 2: return { kind: 'paused', label: 'Awaiting Auth' };
      case 3: return { kind: 'charging', label: 'Charging' };
      case 4: return { kind: 'error', label: 'Error' };
      case 5: return { kind: 'available', label: 'Ready' };
      case 6: return { kind: 'paused', label: 'Paused' };
      case 7: return { kind: 'offline', label: 'Offline' };
    }
  }

  const raw = (state.status || state.chargingStatus || '').toLowerCase();
  if (raw === 'charging') return { kind: 'charging', label: 'Charging' };
  if (raw === 'ready to charge' || raw === 'preparing') return { kind: 'available', label: 'Ready' };
  if (raw === 'available' || raw === 'disconnected') return { kind: 'available', label: 'Available' };
  if (raw === 'error') return { kind: 'error', label: 'Error' };
  if (raw === 'suspendedev' || raw === 'suspendedevse') return { kind: 'paused', label: 'Paused' };

  if (state.isOnline === false) return { kind: 'offline', label: 'Offline' };
  return { kind: 'available', label: raw || 'Unknown' };
}

function displayName(device: DevicePayload): string {
  const meta = device.plugin_metadata || {};
  return (
    device.state?.chargerName ||
    (meta.name as string | undefined) ||
    (meta.chargerId as string | undefined) ||
    device.identifying_string ||
    device.uuid.slice(0, 8)
  );
}

function formatSpotPrice(state: Record<string, any>): string {
  const spot = state.spotPrice;
  if (!spot || typeof spot.costPerKWh !== 'number') return '';
  return `${spot.costPerKWh.toFixed(2)} kr/kWh`;
}

function formatSessions(state: Record<string, any>): { count: number; lastKwh?: string; lastDate?: string; totalKwh?: string } {
  const sessions = state.sessions;
  if (!Array.isArray(sessions) || sessions.length === 0) return { count: 0 };
  const total = sessions.reduce((sum: number, s: any) => sum + (s.energyKwh || 0), 0);
  const last = sessions[0];
  return {
    count: sessions.length,
    lastKwh: last?.energyKwh ? `${last.energyKwh} kWh` : undefined,
    lastDate: last?.sessionDateLocal ? new Date(last.sessionDateLocal).toLocaleDateString() : undefined,
    totalKwh: total > 0 ? `${total.toFixed(1)} kWh total` : undefined,
  };
}

export function DeviceCard({ device, onStart, onStop, busy }: DeviceCardProps) {
  const status = resolveStatus(device);
  const state = device.state || {};
  const isVirtual = device.service_type === 'virtual';
  const isCharger = device.has_sessions !== false;
  const active = !!state.active;
  const name = displayName(device);
  const price = formatSpotPrice(state);
  const sessions = formatSessions(state);
  const cableLocked = typeof state.cableLocked === 'boolean' ? state.cableLocked : null;
  const accessOpen = typeof state.accessOpen === 'boolean' ? state.accessOpen : null;

  return (
    <div class="card device-card">
      <div class="device-card-header">
        <span class={`status-dot ${status.kind}`} aria-label={status.label} />
        <h3 class="device-name">{name}</h3>
        <span class="muted device-service">{device.service_code}</span>
      </div>

      <div class="device-status-label">{status.label}</div>

      <div class="device-meta">
        {isCharger && cableLocked !== null && (
          <span class="device-meta-item">{cableLocked ? '🔒 Cable locked' : '🔓 Cable unlocked'}</span>
        )}
        {isCharger && accessOpen !== null && (
          <span class="device-meta-item">{accessOpen ? 'Access: Open' : 'Access: Restricted'}</span>
        )}
        {isCharger && price && (
          <span class="device-meta-item">{price}</span>
        )}
        {isCharger && sessions.count > 0 && (
          <span class="device-meta-item">{sessions.count} sessions · {sessions.totalKwh}</span>
        )}
        {isCharger && sessions.lastKwh && sessions.lastDate && (
          <span class="device-meta-item muted">Last: {sessions.lastKwh} ({sessions.lastDate})</span>
        )}
        {isVirtual && state.triggerCounter != null && (
          <span class="device-meta-item">Triggered {state.triggerCounter}× </span>
        )}
        {state.isOnline === false && (
          <span class="device-meta-item muted">offline</span>
        )}
        {state.lastError && (
          <span class="device-meta-item device-error">{state.lastError}</span>
        )}
      </div>

      <div class="device-actions">
        <button
          class="btn btn-secondary"
          onClick={() => onStart(device.uuid)}
          disabled={busy || (isVirtual && active)}
          aria-label={`Start ${name}`}
        >
          {busy ? '…' : 'Start'}
        </button>
        <button
          class="btn btn-danger"
          onClick={() => onStop(device.uuid)}
          disabled={busy || (isVirtual && !active)}
          aria-label={`Stop ${name}`}
        >
          Stop
        </button>
      </div>
    </div>
  );
}
